import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, Dropdown, DropdownButton, Form, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import TableLayout from '../../../shared/layout/table/TableLayout';
import { ITableColumn } from '../../../shared/components/TableSkeleton';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { FilterActions } from '../../../shared/components/FilterActions';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import CustomFancybox from '../../../shared/components/Fancybox';
import { OfferPdfService } from '../OfferPdfService';
import { formatDate, getCorrectEnums, hasAccessRight } from '../../../utils/Functions';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { AdminAclEnum } from '../../../enum/AdminAclEnum';
import {
    OfferPdf,
    OfferPdfCampaignAutocomplete,
    OfferPdfTypeEnum,
    OfferPdfUserAutocomplete,
} from '../../../model/offer/OfferPdf';

const initialFilters = {
    page: 1,
    perPage: 15,
    offerId: '',
    campaignId: '',
    userId: '',
    sentById: '',
    type: '',
    totalPages: 0,
    isCurrent: '',
};

const OFFER_PDF_COLUMNS: ITableColumn[] = [
    { label: 'Date' },
    { label: 'Offer ID' },
    { label: 'Offer type' },
    { label: 'Campaign' },
    { label: 'Influencer' },
    { label: 'Sent by' },
    { label: 'Pdf Type' },
    { label: 'Current?' },
    { label: 'Action' },
];

export function OfferPdfList() {
    const [dataLoading, setDataLoading] = useState(true);
    const [offerPdfs, setOfferPdfs] = useState<OfferPdf[]>([]);
    const [filters, setFilters] = useState(initialFilters);
    const [resetKey, setResetKey] = useState(0);
    const [viewingPdfId, setViewingPdfId] = useState<number | null>(null);
    const [regeneratingId, setRegeneratingId] = useState<number | null>(null);

    const [campaignFilter, setCampaignFilter] = useState('');
    const [influencerFilter, setInfluencerFilter] = useState('');
    const [sentByFilter, setSentByFilter] = useState('');

    const [campaignOptions, setCampaignOptions] = useState<OfferPdfCampaignAutocomplete[]>([]);
    const [influencerOptions, setInfluencerOptions] = useState<OfferPdfUserAutocomplete[]>([]);
    const [sentByOptions, setSentByOptions] = useState<OfferPdfUserAutocomplete[]>([]);

    useEffect(() => {
        fetchOfferPdfs(filters);
    }, []);

    useEffect(() => {
        OfferPdfService.getCampaignsAutocomplete(campaignFilter).then(setCampaignOptions).catch(ErrorToast);
    }, [campaignFilter]);

    useEffect(() => {
        OfferPdfService.getInfluencersAutocomplete(influencerFilter).then(setInfluencerOptions).catch(ErrorToast);
    }, [influencerFilter]);

    useEffect(() => {
        OfferPdfService.getSentByAutocomplete(sentByFilter).then(setSentByOptions).catch(ErrorToast);
    }, [sentByFilter]);

    const fetchOfferPdfs = (params: typeof filters, pageOverride?: number) => {
        setDataLoading(true);
        const page = pageOverride ?? params.page;

        OfferPdfService.getOfferPdfs({ ...params, page })
            .then(({ data, page: responsePage, count }) => {
                const totalPages = Math.ceil(count / params.perPage);

                setOfferPdfs(data);
                setFilters(() => ({
                    ...params,
                    page: responsePage,
                    totalPages,
                }));
            })
            .catch(ErrorToast)
            .finally(() => setDataLoading(false));
    };

    const handlePageChange = (page: number) => {
        fetchOfferPdfs(filters, page);
    };

    const handleChange = (evt: React.ChangeEvent<any>) => {
        const { name, value } = evt.target;
        setFilters(prev => ({ ...prev, page: 1, [name]: value }));
    };

    const handleSearch = () => {
        fetchOfferPdfs({ ...filters, page: 1 });
    };

    const handleClear = () => {
        setFilters(initialFilters);
        setCampaignFilter('');
        setInfluencerFilter('');
        setSentByFilter('');
        setResetKey(prev => prev + 1);
        fetchOfferPdfs(initialFilters, 1);
    };

    const hasActiveFilters =
        !!filters.offerId ||
        !!filters.campaignId ||
        !!filters.userId ||
        !!filters.sentById ||
        !!filters.type ||
        !!filters.isCurrent;

    const renderStatusIcon = (condition?: boolean) =>
        condition ? (
            <span className="word-break text-primary">
                <FaCheck />
            </span>
        ) : (
            <span className="word-break text-danger">
                <FaTimes />
            </span>
        );

    const renderWithTooltip = (text?: string) =>
        text ? (
            <OverlayTrigger placement="top" overlay={<Tooltip id="offer-pdf-campaign-tooltip">{text}</Tooltip>}>
                <span className="ml-1">
                    <FiInfo size={14} className="cursor-pointer" />
                </span>
            </OverlayTrigger>
        ) : null;

    const handleRegenerate = (id: number) => {
        setRegeneratingId(id);
        OfferPdfService.regeneratePdf(id)
            .then(() => {
                SuccessToast('PDF regenerated successfully');
                fetchOfferPdfs(filters);
            })
            .catch(ErrorToast)
            .finally(() => setRegeneratingId(null));
    };

    const getOfferType = (offer?: OfferPdf['offer']) => {
        if (!offer) return '';
        if (offer.isAddonsOnly) return 'Addons';
        if (offer.isOnTop) return 'On top';
        if (offer.isLongTerm) return 'Spending';
        return 'Normal';
    };

    return (
        <>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') handleSearch();
                }}
            >
                <Row key={resetKey} style={{ rowGap: '18px' }}>
                    <div className="col-md-2 col-6">
                        <Form.Control
                            placeholder="Offer ID"
                            className="autocomplete-input"
                            name="offerId"
                            value={filters.offerId}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Campaign name or ID"
                            onSelect={setFilters}
                            setFilter={setCampaignFilter}
                            keyState={'campaignId'}
                            keyItemSelect={'id'}
                            value={campaignFilter}
                            items={campaignOptions}
                            hasClearBtn
                            renderItems={['id', 'title']}
                            clearFunc={() => {
                                setFilters(prev => ({ ...prev, campaignId: '' }));
                                setCampaignFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Influencer name or ID"
                            onSelect={setFilters}
                            setFilter={setInfluencerFilter}
                            keyState={'userId'}
                            keyItemSelect={'id'}
                            value={influencerFilter}
                            items={influencerOptions}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setFilters(prev => ({ ...prev, userId: '' }));
                                setInfluencerFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Sent by (name or ID)"
                            onSelect={setFilters}
                            setFilter={setSentByFilter}
                            keyState={'sentById'}
                            keyItemSelect={'id'}
                            value={sentByFilter}
                            items={sentByOptions}
                            hasClearBtn
                            renderItems={['id', 'firstName', 'lastName']}
                            clearFunc={() => {
                                setFilters(prev => ({ ...prev, sentById: '' }));
                                setSentByFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="All types"
                            options={[
                                { value: OfferPdfTypeEnum.INFLUENCER, label: 'Influencer' },
                                { value: OfferPdfTypeEnum.BRAND, label: 'Brand' },
                            ]}
                            selected={String(filters?.type ?? '')}
                            onChange={value => handleChange({ target: { name: 'type', value } } as any)}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="is Current?"
                            options={[
                                { value: 'true', label: 'Current offer' },
                                { value: 'false', label: 'Not current offer' },
                            ]}
                            selected={String(filters?.isCurrent ?? '')}
                            onChange={value => handleChange({ target: { name: 'isCurrent', value } } as any)}
                        />
                    </div>
                </Row>
                <FilterActions onSearch={handleSearch} onClear={handleClear} hasActiveFilters={hasActiveFilters} />
            </Card>

            <TableLayout
                pagination={filters}
                listLength={offerPdfs.length}
                handlePages={handlePageChange}
                isLoading={dataLoading}
                dataName={'offer pdfs'}
                skeletonColumns={OFFER_PDF_COLUMNS}
                skeletonRows={filters.perPage}
            >
                <Table hover responsive>
                    <thead>
                        <tr>
                            {OFFER_PDF_COLUMNS.map(column => (
                                <th key={column.label} className={column.className}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {offerPdfs.map(offerPdf => (
                            <tr key={offerPdf.id}>
                                <td>{formatDate(offerPdf.createdAt)}</td>
                                <td>{offerPdf?.offer?.id}</td>
                                <td>{getOfferType(offerPdf?.offer)}</td>
                                <td>
                                    {offerPdf?.offer?.campaign?.id}
                                    {renderWithTooltip(offerPdf?.offer?.campaign?.title)}
                                </td>
                                <td>
                                    <ImpersonateEye id={offerPdf?.offer?.user?.id ?? 0} />
                                    {offerPdf?.offer?.user?.displayName}
                                </td>
                                <td>
                                    <ImpersonateEye id={offerPdf?.sentBy?.id ?? 0} />
                                    {offerPdf?.sentBy?.firstName} {offerPdf?.sentBy?.lastName}
                                </td>
                                <td>{getCorrectEnums(offerPdf?.type)}</td>
                                <td>{renderStatusIcon(offerPdf?.offer?.isCurrent)}</td>
                                <td>
                                    <DropdownButton title="Actions" id={`offer-pdf-actions-${offerPdf.id}`}>
                                        <Dropdown.Item onClick={() => setViewingPdfId(offerPdf.id)}>View</Dropdown.Item>
                                        <Dropdown.Item onClick={() => window.open(offerPdf.path, '_blank')}>
                                            Download
                                        </Dropdown.Item>
                                        {hasAccessRight(AdminAclEnum.REGENERATE_PDF) && (
                                            <Dropdown.Item
                                                disabled={regeneratingId === offerPdf.id}
                                                onClick={() => handleRegenerate(offerPdf.id)}
                                            >
                                                {regeneratingId === offerPdf.id ? 'Regenerating...' : 'Regenerate PDF'}
                                            </Dropdown.Item>
                                        )}
                                    </DropdownButton>
                                    <CustomFancybox
                                        files={[offerPdf]}
                                        isView
                                        hideTrigger
                                        open={viewingPdfId === offerPdf.id}
                                        onClose={() => setViewingPdfId(null)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableLayout>
        </>
    );
}
